/**
 * The Register tab (TMMAPP-0006 step 3, design §7.8 / §8.5 `register`, MM-D-65; rebuilt to prototype v2 by
 * TMMAPP-0101, audit 06 § B3, v2 L1380–1470): one card — the "Register" header and its sub-line, the
 * toolbar (the shared window, the category filter, Clear, Suggest categories), the lines (served newest
 * first), "Load older lines" and the published-terms footnote. The lines are the envelope's allocations in published terms with a running
 * balance, cursor-paged and filtered by the window and a category. A writer assigns a category inline —
 * every category of the virtual account is offered (MM-D-128), and a grouped line asks whether the change
 * is for the entry or the group; a reader sees the same rows, read-only. Below `md` the table is a card
 * list. Group keys are rendered as served for every reader — the grouping never branches on the viewer.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SuggestCategoriesDialog } from '@/features/ai/SuggestCategoriesDialog';
import { allocationsApi, type CategoryScope } from '@/api/allocations';
import type { Category } from '@/api/categories';
import { ErrorCodes, isApiError } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { ErrorState } from '@/components/ErrorState';
import { useUrlState, type Codec } from '@/shared/useUrlState';
import { registersApi, type EnvelopeRegisterLine } from '@/api/registers';
import { resolveTokens } from '@/theme/theme';
import { CategoryScopeDialog } from './CategoryScopeDialog';
import { RegisterRow } from './RegisterRow';
import { useEnvelopeRange } from './useEnvelopeRange';
import type { WindowDates } from './window';

const PAGE_SIZE = 50;
const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e'] as const;
const FILTER_LABEL_ID = 'envelope-register-category-filter';

type CategoryFilter = 'all' | 'uncategorized' | number;

// `category`: absent = all, `uncategorized`, or a category id (a whole number).
const CATEGORY_CODEC: Codec<CategoryFilter> = {
  default: 'all',
  parse: (raw) => {
    if (raw === 'uncategorized') return raw;
    return /^\d+$/.test(raw) ? Number(raw) : 'all';
  },
  format: String,
};
const URL_SCHEMA = { category: CATEGORY_CODEC };

type PageState =
  | { key: string; status: 'ready'; rows: EnvelopeRegisterLine[]; nextCursor: string | null }
  | { key: string; status: 'error'; error: unknown };

interface ScopePrompt {
  line: EnvelopeRegisterLine;
  categoryId: number | null;
}

interface RegisterTabProps {
  /** The envelope's virtual account: categories and their AI suggestions belong to it (TMMAPI-0145). */
  virtualAccountId: number;
  envelopeId: number;
  /** The virtual account OWNER's currency (§14.1). */
  currency: string | null;
  /** Every category of the virtual account (archived included — a past line may still use one). */
  categories: Category[];
  /** A writer assigns categories inline (the owner before TMMAPP-0110). */
  canWrite: boolean;
  /**
   * The reader owns the virtual account: AI category suggestions are the owner's (consent and bill;
   * TMMAPI-0146 item 7, MM-D-47), so a Manager never reaches them (TMMAPP-0114 A2).
   */
  owner: boolean;
  /** The shared window's dates; `null` while a custom window is invalid (nothing is fetched). */
  dates: WindowDates | null;
  /** The period selector and its resolved dates, owned by the page (it shares them with the P&L). */
  windowControl: ReactNode;
  /** `CATEGORY_NOT_FOUND` / `CONCURRENT_UPDATE`: the page reloads the detail. */
  onCategoriesStale: () => Promise<void>;
  onNotify: (message: string) => void;
  onSubscriptionRequired: () => void;
}

/**
 * The plurality category among the loaded rows sharing `line.groupKey` — the one most peers
 * carry, ties to the first seen (the line itself and uncategorized peers excluded); `null` when
 * no loaded peer carries one. The register is paged,
 * so only what is loaded can be consulted — with no loaded peer there is nothing to follow and
 * the action stays hidden rather than guessing.
 */
function groupCategoryOf(line: EnvelopeRegisterLine, rows: EnvelopeRegisterLine[]): number | null {
  if (line.groupKey === null || (line.groupSize ?? 0) <= 1) return null;
  const votes = new Map<number, number>();
  for (const row of rows) {
    if (row.id === line.id || row.groupKey !== line.groupKey || row.categoryId === null) continue;
    votes.set(row.categoryId, (votes.get(row.categoryId) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestCount = 0;
  for (const [categoryId, count] of votes) {
    if (count > bestCount) {
      best = categoryId;
      bestCount = count;
    }
  }
  return best;
}

export function RegisterTab({
  virtualAccountId,
  envelopeId,
  currency,
  categories,
  canWrite,
  owner,
  dates,
  windowControl,
  onCategoriesStale,
  onNotify,
  onSubscriptionRequired,
}: RegisterTabProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const [{ category: requested }, setUrl] = useUrlState(URL_SCHEMA);
  // The window is the page's shared `from` / `to` (TMMAPP-0114 A3); a set one counts as a register
  // filter for Clear filters.
  const [envelopeRange, setEnvelopeRange] = useEnvelopeRange();
  const rangeSet = envelopeRange.from !== '' || envelopeRange.to !== '';
  const activeCategories = categories.filter((c) => c.archivedAt === null);
  // A category that is not one of the virtual account's active ones (an old link) reads as all.
  const filter: CategoryFilter =
    typeof requested === 'number' && !activeCategories.some((c) => c.id === requested)
      ? 'all'
      : requested;
  const [tick, setTick] = useState(0);
  const [page, setPage] = useState<PageState | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<ScopePrompt | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const from = dates?.from;
  const to = dates?.to;
  // The Uncategorized filter is client-side: the API has no `uncategorized` filter, so the
  // request goes out without `categoryId` and rows carrying one are hidden below.
  const categoryId = typeof filter === 'number' ? filter : undefined;
  // One request per distinct (envelope, window, filter, retry) — the key the page state is tagged with.
  const key =
    dates === null ? null : `${envelopeId}|${from ?? ''}|${to ?? ''}|${String(filter)}|${tick}`;
  // StrictMode double-invokes the effect; a key is fetched once. Responses that outlive a later
  // request are dropped by the sequence.
  const lastKey = useRef<string | null>(null);
  const seq = useRef(0);

  /** Load the first page; then follow cursors while fewer than `count` rows are loaded (the refetch case). */
  const load = useCallback(
    async (count: number) => {
      if (key === null) return;
      const mine = ++seq.current;
      try {
        const query = { from, to, categoryId, limit: PAGE_SIZE };
        let result = await registersApi.envelopeRegister(envelopeId, query);
        const rows = [...result.items];
        while (rows.length < count && result.nextCursor !== null) {
          result = await registersApi.envelopeRegister(envelopeId, {
            ...query,
            cursor: result.nextCursor,
          });
          rows.push(...result.items);
        }
        if (seq.current !== mine) return;
        setPage({ key, status: 'ready', rows, nextCursor: result.nextCursor });
      } catch (error) {
        if (seq.current !== mine) return;
        setPage({ key, status: 'error', error });
      }
    },
    [envelopeId, from, to, categoryId, key],
  );

  useEffect(() => {
    if (key === null || lastKey.current === key) return;
    lastKey.current = key;
    void load(0);
  }, [key, load]);

  const ready = page !== null && page.key === key && page.status === 'ready' ? page : null;
  const failed = page !== null && page.key === key && page.status === 'error' ? page : null;
  const loading = key !== null && ready === null && failed === null;
  const rows = ready?.rows ?? [];
  const visible = filter === 'uncategorized' ? rows.filter((r) => r.categoryId === null) : rows;

  async function handleLoadMore() {
    if (ready === null || ready.nextCursor === null || loadingMore) return;
    const startKey = ready.key;
    setLoadingMore(true);
    try {
      const next = await registersApi.envelopeRegister(envelopeId, {
        from,
        to,
        categoryId,
        cursor: ready.nextCursor,
        limit: PAGE_SIZE,
      });
      // Append only onto the page set the cursor came from (a filter change resets the list).
      setPage((prev) =>
        prev !== null && prev.status === 'ready' && prev.key === startKey
          ? { ...prev, rows: [...prev.rows, ...next.items], nextCursor: next.nextCursor }
          : prev,
      );
    } catch {
      onNotify(t('virtualAccount.errorGeneric'));
    } finally {
      setLoadingMore(false);
    }
  }

  /** Rewrite the loaded rows a patch changed: the line (LINE) or its whole loaded group (GROUP). */
  function applyLocally(
    line: EnvelopeRegisterLine,
    categoryId: number | null,
    scope: CategoryScope,
  ) {
    setPage((prev) => {
      if (prev === null || prev.status !== 'ready') return prev;
      const touched = (row: EnvelopeRegisterLine) =>
        scope === 'GROUP' && line.groupKey !== null
          ? row.groupKey === line.groupKey
          : row.id === line.id;
      return {
        ...prev,
        rows: prev.rows.map((row) => (touched(row) ? { ...row, categoryId } : row)),
      };
    });
  }

  async function patch(
    line: EnvelopeRegisterLine,
    categoryId: number | null,
    scope: CategoryScope,
  ) {
    setBusyId(line.id);
    setEditingId(null);
    try {
      const result = await allocationsApi.patchCategory(line.id, { categoryId, scope });
      applyLocally(line, categoryId, scope);
      // GROUP touched rows this page set may not show (or shows with stale group data): refetch
      // the same number of rows so the list reflects the server.
      if (scope === 'GROUP') await load(rows.length);
      const count =
        result.changedAllocationIds.length > 0
          ? result.changedAllocationIds.length
          : result.updated;
      onNotify(t('envelope.categoryChanged', { count }));
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else if (isApiError(error) && error.code === 'CATEGORY_NOT_FOUND') {
        onNotify(t('virtualAccount.errorGeneric'));
        await onCategoriesStale();
      } else {
        // The shared write-error copy names `CONCURRENT_UPDATE` (the envelope is being moved right now):
        // say so, then refetch the detail and the lines.
        onNotify(errorCopy(t, error, 'virtualAccount.errorGeneric'));
        if (isApiError(error) && error.code === ErrorCodes.CONCURRENT_UPDATE) {
          await onCategoriesStale();
          setTick((n) => n + 1);
        }
      }
    } finally {
      setBusyId(null);
    }
  }

  function handleSelectCategory(line: EnvelopeRegisterLine, categoryId: number | null) {
    if (categoryId === line.categoryId || busyId !== null) {
      setEditingId(null);
      return;
    }
    // A grouped line asks whether the change is for this entry or the whole group (A2).
    if (line.groupSize !== null && line.groupSize > 1) {
      setEditingId(null);
      setPrompt({ line, categoryId });
    } else void patch(line, categoryId, 'LINE');
  }

  const filterValue = typeof filter === 'number' ? String(filter) : filter;
  const originState = { from: 'envelope', virtualAccountId, envelopeId } as const;

  const renderRow = (line: EnvelopeRegisterLine) => {
    const groupCategory = canWrite ? (line.groupCategoryId ?? groupCategoryOf(line, rows)) : null;
    const canFollow = groupCategory !== null && groupCategory !== line.categoryId;
    return (
      <RegisterRow
        key={line.id}
        line={line}
        currency={currency}
        categories={categories}
        writer={canWrite}
        busy={busyId === line.id}
        wide={md}
        editing={editingId === line.id}
        onStartEdit={() => setEditingId(line.id)}
        onStopEdit={() => setEditingId((current) => (current === line.id ? null : current))}
        onSelectCategory={(next) => handleSelectCategory(line, next)}
        originState={originState}
        {...(canFollow ? { onFollowGroup: () => void patch(line, groupCategory, 'LINE') } : {})}
      />
    );
  };

  return (
    <Paper component="section" aria-labelledby="register-title" sx={{ p: 0, overflow: 'hidden' }}>
      {/* One compact header row (the 0101 review): the title, and the toolbar end-aligned beside it
          at `md`+, stacked beneath it below `md`. */}
      <Stack
        data-card-header=""
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          p: '16px 20px',
          gap: 1.5,
          alignItems: { md: 'center' },
          justifyContent: 'space-between',
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography id="register-title" variant="h3" component="h2">
            {t('envelope.tabRegister')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {t('envelope.registerSub')}
          </Typography>
        </Box>
        <Stack
          direction="row"
          data-register-toolbar=""
          sx={{
            gap: 1.5,
            // One row from `md` up, as the account register's (TMMAPP-0105); stacked below.
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id={FILTER_LABEL_ID}>{t('envelope.filterCategory')}</InputLabel>
            <Select
              labelId={FILTER_LABEL_ID}
              label={t('envelope.filterCategory')}
              value={filterValue}
              onChange={(event) => {
                const value = event.target.value;
                setUrl({
                  category: value === 'all' || value === 'uncategorized' ? value : Number(value),
                });
              }}
            >
              <MenuItem value="all">{t('envelope.allCategories')}</MenuItem>
              {activeCategories.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
              <MenuItem value="uncategorized">{t('envelope.uncategorized')}</MenuItem>
            </Select>
          </FormControl>
          <ClearFiltersButton
            active={(rangeSet ? 1 : 0) + (filter !== 'all' ? 1 : 0)}
            onClear={() => {
              setEnvelopeRange({ from: '', to: '' });
              setUrl({ category: 'all' });
            }}
          />
          {canWrite && owner && visible.some((row) => row.categoryId === null) && (
            <Button variant="outlined" size="small" onClick={() => setSuggestOpen(true)}>
              {t('envelope.suggestCategories')}
            </Button>
          )}
        </Stack>
      </Stack>
      {/* The window's chips get their own row under the header, as the virtual account P&L's do
          (TMMAPP-0114 A3): six chips and their dates don't fit beside the filter. */}
      <Box data-register-range="" sx={{ p: '12px 20px', borderBottom: `1px solid ${tokens.line}` }}>
        {windowControl}
      </Box>

      {failed !== null && (
        <Box sx={{ p: '0 20px 20px' }}>
          <ErrorState
            variant="section"
            error={failed.error}
            what={t('errors.what.envelopeRegister')}
            onRetry={() => setTick((n) => n + 1)}
          />
        </Box>
      )}

      {loading && (
        <Stack spacing={1} aria-busy="true" sx={{ p: '0 20px 16px' }}>
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} variant="rounded" height={36} />
          ))}
        </Stack>
      )}

      {ready !== null && visible.length === 0 && (
        <Typography
          data-register-empty=""
          sx={{ p: '22px 20px', fontSize: 13, color: tokens.text2 }}
        >
          {t('envelope.registerEmpty')}
        </Typography>
      )}

      {ready !== null &&
        visible.length > 0 &&
        (md ? (
          <TableContainer sx={{ overflowX: 'auto', borderTop: `1px solid ${tokens.line}` }}>
            <Table size="dense" aria-label={t('envelope.tabRegister')} sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: tokens.surface2 }}>
                  <TableCell>{t('envelope.colDate')}</TableCell>
                  <TableCell>{t('envelope.colSource')}</TableCell>
                  <TableCell>{t('envelope.colOwner')}</TableCell>
                  <TableCell>{t('virtualAccount.ledger.colDescription')}</TableCell>
                  <TableCell>{t('envelope.colCategory')}</TableCell>
                  <TableCell align="right">{t('envelope.colAmount')}</TableCell>
                  <TableCell align="right">{t('envelope.colBalance')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{visible.map(renderRow)}</TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            role="list"
            aria-label={t('envelope.tabRegister')}
            sx={{ borderTop: `1px solid ${tokens.line}` }}
          >
            {visible.map((line) => (
              <Box role="listitem" key={line.id}>
                {renderRow(line)}
              </Box>
            ))}
          </Box>
        ))}

      {ready !== null && ready.nextCursor !== null && (
        <Stack direction="row" sx={{ justifyContent: 'center', p: '12px 20px 16px' }}>
          <Button variant="outlined" onClick={() => void handleLoadMore()} loading={loadingMore}>
            {t('common.loadOlderLines')}
          </Button>
        </Stack>
      )}

      {ready !== null && (
        <Typography
          data-register-footnote=""
          sx={{
            p: '12px 20px',
            borderTop: `1px solid ${tokens.line}`,
            fontSize: 12,
            color: tokens.textLabel('surface'),
          }}
        >
          {t('envelope.registerFootnote')}
        </Typography>
      )}

      {suggestOpen && (
        <SuggestCategoriesDialog
          virtualAccountId={virtualAccountId}
          lines={visible.filter((row) => row.categoryId === null)}
          categories={categories}
          onClose={() => setSuggestOpen(false)}
          onApplied={() => {
            setSuggestOpen(false);
            setTick((n) => n + 1);
          }}
          onNotify={onNotify}
        />
      )}

      {prompt !== null && (
        <CategoryScopeDialog
          count={prompt.line.groupSize ?? 1}
          mixed={prompt.line.groupCategoryId === null && (prompt.line.groupSize ?? 0) > 1}
          categoryName={
            prompt.categoryId === null
              ? null
              : (categories.find((category) => category.id === prompt.categoryId)?.name ?? null)
          }
          onClose={() => setPrompt(null)}
          onChoose={(scope) => {
            const { line, categoryId } = prompt;
            setPrompt(null);
            void patch(line, categoryId, scope);
          }}
        />
      )}
    </Paper>
  );
}
